import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Autocomplete,
  InputAdornment,
  Chip,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Popper,
  Grow,
} from '@mui/material';
import {
  Search,
  History,
  TrendingUp,
  Folder,
  Person,
  CalendarToday,
  Label,
  Clear,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface SearchSuggestion {
  type: 'file' | 'user' | 'date' | 'tag' | 'history';
  label: string;
  value: string;
  icon: React.ReactNode;
  secondary?: string;
}

interface SearchAutocompleteProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  searchHistory?: string[];
  files?: Array<{ fileName: string; uploadedBy?: string; uploadedAt: string; tags?: string[] }>;
}

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  onSearch,
  placeholder = 'ファイル名、アップロード者、タグで検索...',
  searchHistory = [],
  files = [],
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>(searchHistory);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 検索履歴を保存
  const saveToHistory = (query: string) => {
    if (query && !recentSearches.includes(query)) {
      const newHistory = [query, ...recentSearches].slice(0, 5);
      setRecentSearches(newHistory);
      // LocalStorageに保存
      localStorage.setItem('mederi_search_history', JSON.stringify(newHistory));
    }
  };

  // 検索候補を生成
  const generateSuggestions = (query: string): SearchSuggestion[] => {
    if (!query) {
      // 検索履歴とトレンドを表示
      const historySuggestions = recentSearches.slice(0, 3).map(item => ({
        type: 'history' as const,
        label: item,
        value: item,
        icon: <History fontSize="small" />,
        secondary: '検索履歴',
      }));

      const trendingSuggestions = [
        { label: '本日のアップロード', value: 'date:today' },
        { label: '処理中のファイル', value: 'status:processing' },
        { label: 'エラーファイル', value: 'status:error' },
      ].map(item => ({
        type: 'file' as const,
        label: item.label,
        value: item.value,
        icon: <TrendingUp fontSize="small" color="primary" />,
        secondary: 'トレンド',
      }));

      return [...historySuggestions, ...trendingSuggestions];
    }

    const lowerQuery = query.toLowerCase();
    const results: SearchSuggestion[] = [];

    // ファイル名での検索
    files
      .filter(file => file.fileName.toLowerCase().includes(lowerQuery))
      .slice(0, 3)
      .forEach(file => {
        results.push({
          type: 'file',
          label: file.fileName,
          value: file.fileName,
          icon: <Folder fontSize="small" color="primary" />,
          secondary: `${format(new Date(file.uploadedAt), 'yyyy/MM/dd', { locale: ja })}`,
        });
      });

    // アップロード者での検索
    const uniqueUsers = Array.from(new Set(files.map(f => f.uploadedBy).filter(Boolean)));
    uniqueUsers
      .filter(user => user!.toLowerCase().includes(lowerQuery))
      .slice(0, 2)
      .forEach(user => {
        results.push({
          type: 'user',
          label: user!,
          value: `user:${user}`,
          icon: <Person fontSize="small" color="secondary" />,
          secondary: 'アップロード者',
        });
      });

    // タグでの検索
    const allTags = Array.from(new Set(files.flatMap(f => f.tags || [])));
    allTags
      .filter(tag => tag.toLowerCase().includes(lowerQuery))
      .slice(0, 2)
      .forEach(tag => {
        results.push({
          type: 'tag',
          label: tag,
          value: `tag:${tag}`,
          icon: <Label fontSize="small" style={{ color: theme.palette.success.main }} />,
          secondary: 'タグ',
        });
      });

    // 日付での検索（例: "2024", "1月"）
    if (/\d{4}/.test(query) || /\d{1,2}月/.test(query)) {
      results.push({
        type: 'date',
        label: `日付: ${query}`,
        value: `date:${query}`,
        icon: <CalendarToday fontSize="small" color="info" />,
        secondary: '日付フィルター',
      });
    }

    return results;
  };

  useEffect(() => {
    setSuggestions(generateSuggestions(inputValue));
  }, [inputValue, files, recentSearches]);

  useEffect(() => {
    // LocalStorageから検索履歴を読み込み
    const savedHistory = localStorage.getItem('mederi_search_history');
    if (savedHistory) {
      setRecentSearches(JSON.parse(savedHistory));
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setIsOpen(true);
    onSearch(event.target.value);
  };

  const handleSuggestionSelect = (suggestion: SearchSuggestion | null) => {
    if (suggestion) {
      setInputValue(suggestion.label);
      saveToHistory(suggestion.label);
      onSearch(suggestion.value);
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onSearch('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue) {
      saveToHistory(inputValue);
      setIsOpen(false);
    }
  };

  const CustomPopper = (props: any) => (
    <Popper {...props} transition placement="bottom-start">
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}>
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
              background: alpha(theme.palette.background.paper, 0.98),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 2,
            }}
          >
            {props.children}
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        freeSolo
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        inputValue={inputValue}
        options={suggestions}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_, value) => handleSuggestionSelect(value as SearchSuggestion)}
        PopperComponent={CustomPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={placeholder}
            variant="outlined"
            size="medium"
            fullWidth
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <AnimatePresence>
                    {inputValue && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                      >
                        <IconButton size="small" onClick={handleClear}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                '& fieldset': {
                  borderColor: alpha(theme.palette.divider, 0.2),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <ListItem
            {...props}
            sx={{
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={500}>
                  {option.label}
                </Typography>
              }
              secondary={
                option.secondary && (
                  <Typography variant="caption" color="text.secondary">
                    {option.secondary}
                  </Typography>
                )
              }
            />
          </ListItem>
        )}
        renderGroup={(params) => (
          <Box key={params.key}>
            {params.group && (
              <>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ px: 2, py: 1, display: 'block' }}
                >
                  {params.group}
                </Typography>
                <Divider />
              </>
            )}
            {params.children}
          </Box>
        )}
      />

      {/* 検索フィルターチップ */}
      <AnimatePresence>
        {inputValue && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ position: 'absolute', top: '100%', marginTop: 8, zIndex: 1 }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {inputValue.includes(':') && (
                <Chip
                  size="small"
                  label={inputValue}
                  onDelete={handleClear}
                  color="primary"
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  }}
                />
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default SearchAutocomplete;